// requestInterceptor.js
import authTokenHandler from '@/utils/authTokenHandler';
import tokenService from '@/data/api/tokenService';
import axiosInstance from '@/data/api/axiosInstance';
import tokenRequestQueue from '@/data/request/tokenRequestQueue';

/**
 * Request Interceptor with Concurrency Handling
 */
export const requestInterceptor = (config) => {
  // Public auth routes must be reachable before any token exists in storage.
  if (authTokenHandler.shouldBypassTokenHandling(config.url)) {
    return config;
  }

  // 2. Retrieve the stored token
  return authTokenHandler
    .retrieveToken()
    .then((tokenData) => {
      // 2a. If the token is still valid, just attach it
      if (authTokenHandler.isTokenValid(tokenData)) {
        config.headers.Authorization = `Bearer ${tokenData.access_token}`;
        return config;
      }

      // 2b. The token is not valid. We need to refresh:
      //     Check if a refresh is already in progress
      if (!tokenRequestQueue.isRefreshing) {
        // No refresh in progress -> start now
        tokenRequestQueue.isRefreshing = true;

        return tokenService
          .refreshToken(axiosInstance) // Attempt to refresh
          .then(() => authTokenHandler.retrieveToken()) // Retrieve the fresh token
          .then((newTokenData) => {
            const newAccessToken = newTokenData?.access_token || null;

            // Notify all queued requests that refresh succeeded
            tokenRequestQueue.processQueue(null, newAccessToken);

            // Attach new token if we have one
            if (newAccessToken) {
              config.headers.Authorization = `Bearer ${newAccessToken}`;
            }
            return config;
          })
          .catch((refreshError) => {
            // Refresh failed: notify queued requests
            tokenRequestQueue.processQueue(refreshError, null);

            // Optionally, do logout/redirect logic here
            return Promise.reject(refreshError);
          })
          .finally(() => {
            // Allow future refresh attempts
            tokenRequestQueue.isRefreshing = false;
          });
      }

      // 2c. If we're already refreshing, queue this request until the refresh finishes
      return new Promise((resolve, reject) => {
        tokenRequestQueue.enqueue((newToken) => {
          // Called when refresh is successful
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
          resolve(config);
        }, reject);
      });
    })
    .catch((error) => {
      // If retrieving the token failed entirely (e.g., not stored),
      // we can either attach nothing or re-throw based on your needs.
      console.warn('Failed to retrieve or refresh token');
      // abort the request and handle as an error upstream.
      throw error;
    });
};

/**
 * Request error interceptor:
 * Called if something goes wrong before sending the request
 */
export const requestInterceptorError = (error) => {
  return Promise.reject(error);
};
